import { Flex } from '@chakra-ui/layout';
import {
  Card,
  CardBody,
  FormControl,
  FormLabel,
  Switch,
  useColorModeValue,
} from '@chakra-ui/react';
import { isEqual } from 'lodash';
import React from 'react';
import {
  useParams,
  generatePath,
  useMatch,
  Link,
  useLocation,
  matchPath,
} from 'react-router-dom';
import { z } from 'zod';

import {
  MenuTree,
  MenuTreeItem,
} from 'packages/common-frontend/components/MenuTree';
import { Typography } from 'packages/common-frontend/components/Typography';
import { useCreditAgreementStore } from '~/core/routes/settings/implementations/common/CreditAgreementProvider/CreditAgreementProvider';
import {
  getCommitments,
  getExpenses,
  getFacilityItems,
  getParties,
  getPricingOptions,
} from '~/core/routes/settings/implementations/layout/NavMenu/dataMappings';

const ParamSchema = z.object({
  implementationId: z.string(),
  facilityId: z.string(),
});

type NavItemProps = {
  path: string;
  label: string;
  children?: React.ReactNode;
  forceOpen?: boolean;
};

export const NavItem = ({ path, label, children, forceOpen }: NavItemProps) => {
  const params = useParams();
  const parsedParams = ParamSchema.parse(params);

  const fullPath = `/settings/implementations/:implementationId/facilities/:facilityId/${path}`;

  const to = generatePath(fullPath, parsedParams);
  const isNavItemActive = !!useMatch({ path: fullPath });
  const isFullMatch = !!useMatch({ path: fullPath, end: false });
  const isChildActive = forceOpen || isFullMatch;

  return (
    <MenuTreeItem
      ButtonProps={{ as: Link, to }}
      label={label}
      isActive={isNavItemActive}
      defaultIsOpen
      fullSurfaceExpandsContent={false}
    >
      {isChildActive && children}
    </MenuTreeItem>
  );
};

export const NavMenu = () => {
  const borderColor = useColorModeValue('gray.100', 'gray.800');

  const facilityItems = useCreditAgreementStore(getFacilityItems, isEqual);
  const parties = useCreditAgreementStore(getParties, isEqual);
  const pricingOptions = useCreditAgreementStore(getPricingOptions, isEqual);
  const commitments = useCreditAgreementStore(getCommitments, isEqual);
  const expenses = useCreditAgreementStore(getExpenses, isEqual);

  const isEditing = useCreditAgreementStore((store) => store.isEditing);
  const showOnlyChangesInNav = useCreditAgreementStore(
    (store) => store.showOnlyChangesInNav,
  );
  const setShowOnlyChangesInNav = useCreditAgreementStore(
    (store) => store.setShowOnlyChangesInNav,
  );

  const forceMenuOpen = !isEditing && showOnlyChangesInNav;

  const { pathname } = useLocation();

  const hide = matchPath(
    '/settings/implementations/:implementationId/facilities/:facilityId',
    pathname,
  );

  if (hide) return null;

  return (
    <Flex width="16rem" flexShrink="0" direction="column">
      {!isEditing && (
        <Card
          variant="outline"
          size="sm"
          mb="1rem"
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="0.5rem"
        >
          <CardBody>
            <FormControl display="flex" alignItems="center">
              <Switch
                id="show-changes"
                isChecked={showOnlyChangesInNav}
                onChange={() => setShowOnlyChangesInNav(!showOnlyChangesInNav)}
              />
              <FormLabel htmlFor="show-changes" paddingLeft="0.6rem" mb={0}>
                <Typography variant="detail" fontWeight="bold">
                  Only Show Changes
                </Typography>
              </FormLabel>
            </FormControl>
          </CardBody>
        </Card>
      )}
      <MenuTree
        FlexProps={{
          borderWidth: '1px',
          borderColor,
          borderRadius: '0.5rem',
          padding: '0.75rem',
        }}
      >
        <NavItem
          path="facility-mechanics"
          label="Facility"
          forceOpen={forceMenuOpen}
        >
          {facilityItems.map((facilityProperty) => (
            <NavItem
              key={facilityProperty.anchor}
              path={`facility-mechanics#${facilityProperty.anchor}`}
              label={facilityProperty.label}
            />
          ))}
        </NavItem>
        <NavItem path="parties" label="Parties" forceOpen={forceMenuOpen}>
          {parties.map((party) => (
            <NavItem
              key={party.anchor}
              path={`parties#${party.anchor}`}
              label={party.label}
            />
          ))}
        </NavItem>
        <NavItem
          path="pricing-options"
          label="Pricing Options"
          forceOpen={forceMenuOpen}
        >
          {pricingOptions.map((pricingOption) => (
            <NavItem
              key={pricingOption.key}
              path={`pricing-options/${pricingOption.key}`}
              label={pricingOption.label}
            />
          ))}
        </NavItem>
        <NavItem
          path="commitments"
          label="Commitments"
          forceOpen={forceMenuOpen}
        >
          {commitments.map((commitment) => (
            <NavItem
              key={commitment.id}
              path={`commitments/${commitment.id}`}
              label={commitment.label}
            />
          ))}
        </NavItem>
        <NavItem
          path="expense-mechanics"
          label="Expenses & Fees"
          forceOpen={forceMenuOpen}
        >
          {expenses.map((expense) => (
            <NavItem
              key={expense.counterpartySlug}
              path={`expenses-mechanics/unusedFee/${expense.counterpartySlug}`}
              label={expense.label}
            />
          ))}
        </NavItem>
      </MenuTree>
    </Flex>
  );
};
